import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearch, useLocation } from "wouter";
import { AlertTriangle, Check, CheckSquare, ChevronDown, Download, FolderOpen, Loader2, Pencil, Plus, Search, Settings2, Square, Tags, Trash2, X } from "lucide-react";
import { ExportDialog } from "../components/ExportDialog";
import { AnalyzeButton } from "../components/AnalyzeMenu";
import { AddToCollection, useCollections } from "../components/Collections";
import { BulkEdit } from "../components/BulkEdit";
import { aiBusy, api, img, STATUS_LABEL, type Collection, type Facets, type ListItem } from "../lib/api";
import { useApp } from "../App";

const ISSUE_LABEL: Record<string, string> = {
  alt: "Saknar alt-text", consent: "Samtycke ej klart", license: "Licens saknas",
  expiring: "Licens går ut inom 30 dagar", suggestions: "Har obehandlade förslag", ai_text: "AI-text att granska", unanalysed: "Ej AI-analyserade", draft: "Utkast",
};

/** Filtren ligger i URL:en så att en sökning går att dela med en kollega. */
function useFilters() {
  const search = useSearch();
  const [, navigate] = useLocation();
  const p = new URLSearchParams(search);
  const set = (k: string, v: string | null) => {
    const n = new URLSearchParams(search);
    if (v) n.set(k, v); else n.delete(k);
    navigate(`/?${n}`, { replace: true });
  };
  const tags = (p.get("tags") ?? "").split(",").filter(Boolean);
  return {
    q: p.get("q") ?? "", tags, status: p.get("status"), orientation: p.get("orientation"),
    photographer: p.get("photographer"), issue: p.get("issue"), color: p.get("color"), collection: p.get("collection"),
    query: p.toString(), set,
    toggleTag: (t: string) => set("tags", (tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]).join(",") || null),
  };
}

export function Library() {
  const f = useFilters();
  const { version, bump, isAdmin } = useApp();
  const [data, setData] = useState<{ items: ListItem[]; facets: Facets; total: number } | null>(null);
  const [q, setQ] = useState(f.q);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => { setQ(f.q); }, [f.q]);
  useEffect(() => {
    const t = setTimeout(() => { if (q !== f.q) f.set("q", q || null); }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let alive = true;
    api<typeof data>(`assets?${f.query}`).then((d) => alive && setData(d));
    return () => { alive = false; };
  }, [f.query, version]);

  // Så länge någon bild analyseras: fråga igen tills förslagen finns.
  const analysing = data?.items.some((i) => i.analysis === "pending");
  const aiRunning = data?.items.some(aiBusy);
  useEffect(() => {
    if (!analysing && !aiRunning) return;
    const t = setInterval(bump, analysing ? 2000 : 4000);
    return () => clearInterval(t);
  }, [analysing, aiRunning, bump]);

  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const { list: collections, reload: reloadCollections } = useCollections(version);
  const current = collections?.find((c) => c.id === f.collection) ?? null;
  const [exportAll, setExportAll] = useState(false);

  return (
    <div className="grid gap-6 pt-6 lg:grid-cols-[240px_1fr]">
      <aside className="hidden space-y-6 lg:block lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-2">
        {(isAdmin || !!collections?.length) && (
          <Facet title="Samlingar">
            {collections?.map((c) => (
              <FacetRow key={c.id} label={c.name} n={c.count} active={f.collection === c.id}
                onClick={() => f.set("collection", f.collection === c.id ? null : c.id)} />
            ))}
            {isAdmin && <NewCollection onCreated={(id) => { reloadCollections(); f.set("collection", id); }} />}
          </Facet>
        )}
        {!!data?.facets.colors?.length && (
          <Facet title="Färg">
            <div className="flex flex-wrap gap-1.5 px-1 pt-1">
              {data.facets.colors.map((c) => (
                <button key={c.name} onClick={() => f.set("color", f.color === c.name ? null : c.name)} title={`${c.name} (${c.n})`}
                  aria-label={`Färg: ${c.name}`} aria-pressed={f.color === c.name}
                  className={`h-7 w-7 rounded-full border transition ${f.color === c.name ? "ring-2 ring-ember ring-offset-2" : "border-black/10 hover:scale-110"}`}
                  style={{ background: c.hex }} />
              ))}
            </div>
            {f.color && <button className="mt-1 px-2 text-[12.5px] text-soft hover:text-ink" onClick={() => f.set("color", null)}>Färg: {f.color} ✕</button>}
          </Facet>
        )}
        {isAdmin && <Facet title="Status">
          {["publicerad", "utkast", "arkiverad"].map((s) => (
            <FacetRow key={s} label={STATUS_LABEL[s]!} active={f.status === s} onClick={() => f.set("status", f.status === s ? null : s)} />
          ))}
        </Facet>}
        <Facet title="Format">
          {["liggande", "stående", "kvadratisk"].map((s) => (
            <FacetRow key={s} label={s[0]!.toUpperCase() + s.slice(1)} active={f.orientation === s} onClick={() => f.set("orientation", f.orientation === s ? null : s)} />
          ))}
        </Facet>
        {isAdmin && <Facet title="Behöver åtgärdas">
          {Object.entries(ISSUE_LABEL).map(([k, v]) => (
            <FacetRow key={k} label={v} active={f.issue === k} onClick={() => f.set("issue", f.issue === k ? null : k)} />
          ))}
        </Facet>}
        {!!data?.facets.photographers.length && (
          <Facet title="Fotograf">
            {data.facets.photographers.map((p) => (
              <FacetRow key={p.name} label={p.name} n={p.n} active={f.photographer === p.name} onClick={() => f.set("photographer", f.photographer === p.name ? null : p.name)} />
            ))}
          </Facet>
        )}
      </aside>

      <section className="min-w-0">
        {isAdmin && !current && <ExpiringBanner />}
        {current && (
          <CollectionHeader c={current} total={data?.total ?? 0} isAdmin={isAdmin}
            onExport={() => setExportAll(true)} onClose={() => f.set("collection", null)}
            onChanged={() => { reloadCollections(); bump(); }} onDeleted={() => { reloadCollections(); f.set("collection", null); }} />
        )}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sök på motiv, plats, fotograf, text i bilden…"
              className="field !rounded-xl !py-3 !pl-11 text-[15px]" />
          </div>
          <TagPicker tags={data?.facets.tags ?? []} selected={f.tags} onToggle={f.toggleTag} onClear={() => f.set("tags", null)} />
        </div>

        {f.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {f.tags.map((t) => (
              <button key={t} onClick={() => f.toggleTag(t)} className="chip !border-ink !bg-ink text-white">
                {t} <X size={12} />
              </button>
            ))}
          </div>
        )}

        <div className="mb-3 mt-5 flex items-center justify-between text-soft">
          <span>{data ? `${data.total} bild${data.total === 1 ? "" : "er"}` : "Laddar…"}</span>
          {analysing && <span className="flex items-center gap-1.5 text-ember"><Loader2 size={14} className="animate-spin" /> Analyserar nya bilder…</span>}
        </div>

        {data && data.total === 0 && <Empty filtered={!!f.query} />}

        <div className="columns-2 gap-3 sm:columns-3 xl:columns-4 2xl:columns-5">
          {data?.items.map((a) => (
            <Tile key={a.id} a={a} selected={selected.has(a.id)} selecting={selected.size > 0} onToggle={() => toggle(a.id)} />
          ))}
        </div>
      </section>

      {selected.size > 0 && (
        <BulkBar ids={[...selected]} collection={current} onDone={() => { setSelected(new Set()); bump(); reloadCollections(); }}
          onClear={() => setSelected(new Set())} onExport={() => setExporting(true)} />
      )}
      {exportAll && data && (
        <ExportDialog onClose={() => setExportAll(false)} items={data.items} />
      )}
      {exporting && (
        <ExportDialog onClose={() => setExporting(false)}
          items={[...selected].map((id) => data?.items.find((i) => i.id === id) ?? { id })} />
      )}
    </div>
  );
}

function Tile({ a, selected, selecting, onToggle }: { a: ListItem; selected: boolean; selecting: boolean; onToggle: () => void }) {
  const { isAdmin } = useApp();
  return (
    // Ingen overflow-hidden på kortet – då skulle åtgärdslistan klippas på låga bilder. Kortet lyfts över grannarna vid hover.
    <div className={`group relative mb-3 break-inside-avoid rounded-xl border bg-card transition hover:z-20 ${selected ? "border-ember ring-2 ring-ember/40" : "border-line hover:shadow-md"}`}>
      <Link href={`/bild/${a.id}`} onClick={(e) => { if (selecting) { e.preventDefault(); onToggle(); } }}>
        <div className="overflow-hidden rounded-t-[11px]" style={{ aspectRatio: `${a.width}/${a.height}`, background: `${a.color} url(${a.lqip}) center/cover` }}>
          <img src={img(a, 480)} alt={a.alt} loading="lazy" className="h-full w-full object-cover" />
        </div>
        <div className="px-3 py-2">
          <div className="truncate font-medium">{a.title || <span className="text-muted">Namnlös</span>}</div>
          {isAdmin && <div className="mt-1 flex flex-wrap items-center gap-1">
            {a.status !== "publicerad" && <Badge tone={a.status === "arkiverad" ? "muted" : "warn"}>{STATUS_LABEL[a.status]}</Badge>}
            {a.status === "publicerad" && a.blocked && <Badge tone="bad">Dold: {a.blocked}</Badge>}
            {a.analysis === "pending" && <Badge tone="muted"><Loader2 size={11} className="animate-spin" /> analyseras</Badge>}
            {(a.vision === "pending" || a.vision === "claimed") && <Badge tone="muted"><Loader2 size={11} className="animate-spin" /> Apple Vision</Badge>}
            {a.claude === "pending" && <Badge tone="muted"><Loader2 size={11} className="animate-spin" /> Claude</Badge>}
          </div>}
          {!isAdmin && a.photographer && <div className="truncate text-[12px] text-muted">Foto: {a.photographer}</div>}
        </div>
      </Link>
      {isAdmin && a.issues?.length > 0 && <IssueFlag issues={a.issues} />}
      <button onClick={onToggle} aria-label="Markera"
        className={`absolute left-2 top-2 rounded-md bg-card/90 p-1 shadow transition ${selected || selecting ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
        {selected ? <CheckSquare size={18} className="text-ember" /> : <Square size={18} className="text-soft" />}
      </button>
    </div>
  );
}

/**
 * Utropstecken i bildens hörn när något behöver göras. Rött = stoppar publicering, gult = bör ses över.
 * Håll muspekaren över (eller fokusera) för att se listan.
 */
function IssueFlag({ issues }: { issues: ListItem["issues"] }) {
  const blocking = issues.some((i) => i.level === "block");
  return (
    <div className="group/flag absolute right-2 top-2 z-10" tabIndex={0} aria-label={`${issues.length} saker att åtgärda`}>
      <div className={`grid h-7 min-w-7 place-items-center rounded-full px-1.5 text-[13px] font-bold text-white shadow-md ring-2 ring-white
        ${blocking ? "bg-bad" : "bg-warn"}`}>
        {issues.length > 1 ? issues.length : "!"}
      </div>
      <div className="pointer-events-none absolute right-0 top-9 w-56 rounded-lg bg-ink/95 p-2.5 text-[12.5px] text-white opacity-0 shadow-xl transition
        group-hover/flag:opacity-100 group-focus/flag:opacity-100">
        <div className="mb-1 font-semibold">Att åtgärda</div>
        <ul className="space-y-1">
          {issues.map((i) => (
            <li key={i.key + i.label} className="flex gap-1.5">
              <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${i.level === "block" ? "bg-red-400" : "bg-amber-300"}`} />
              {i.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Rullista med alla taggar i träffmängden, grupperade per kategori. */
function TagPicker({ tags, selected, onToggle, onClear }: {
  tags: Facets["tags"]; selected: string[]; onToggle: (t: string) => void; onClear: () => void;
}) {
  const { isAdmin } = useApp();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, []);
  const shown = tags.filter((t) => t.name.includes(filter.trim().toLowerCase()));
  const groups = useMemo(() => {
    const m = new Map<string, Facets["tags"]>();
    for (const t of shown) (m.get(t.category) ?? m.set(t.category, []).get(t.category)!).push(t);
    return [...m].sort((a, b) => (a[0] === "motiv" ? -1 : b[0] === "motiv" ? 1 : a[0].localeCompare(b[0], "sv")));
  }, [shown]);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)}
        className={`flex h-full items-center gap-2 rounded-xl border px-4 font-medium transition ${selected.length ? "border-ink bg-ink text-white" : "border-line bg-card hover:bg-mist"}`}>
        <Tags size={16} /> Taggar{selected.length ? ` (${selected.length})` : ""} <ChevronDown size={14} className={open ? "rotate-180" : ""} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-2 flex max-h-[70vh] w-80 flex-col overflow-hidden rounded-xl border border-line bg-card shadow-xl">
          <div className="border-b border-line p-2">
            <input autoFocus className="field !py-1.5" placeholder="Filtrera taggar…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
          <div className="overflow-y-auto p-2">
            {groups.length === 0 && <p className="p-2 text-muted">Inga taggar.</p>}
            {groups.map(([cat, list]) => (
              <div key={cat} className="mb-2">
                <div className="h-section px-2 py-1">{cat}</div>
                {list.map((t) => {
                  const on = selected.includes(t.name);
                  return (
                    <button key={t.name} onClick={() => onToggle(t.name)}
                      className={`flex w-full items-center justify-between rounded-md px-2 py-1 text-left ${on ? "bg-ember-soft font-medium text-ember-2" : "hover:bg-mist"}`}>
                      <span className="flex items-center gap-2">
                        <span className={`grid h-4 w-4 place-items-center rounded border ${on ? "border-ember bg-ember text-white" : "border-line"}`}>{on && <Check size={11} />}</span>
                        {t.name}
                      </span>
                      <span className="text-muted">{t.n}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          {(selected.length > 0 || isAdmin) && (
            <div className="flex items-center justify-between border-t border-line px-3 py-2 text-[13px]">
              {selected.length > 0 ? <button className="text-soft hover:text-ink" onClick={onClear}>Rensa val</button> : <span />}
              {isAdmin && <Link href="/taggar" className="flex items-center gap-1 text-soft hover:text-ink"><Settings2 size={13} /> Hantera taggar</Link>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NewCollection({ onCreated }: { onCreated: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  if (!open) return (
    <button onClick={() => setOpen(true)} className="flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-soft hover:bg-mist">
      <Plus size={13} /> Ny samling
    </button>
  );
  return (
    <form className="px-1 pt-1" onSubmit={async (e) => {
      e.preventDefault();
      if (!name.trim()) return;
      const r = await api<{ id: string }>("collections", { method: "POST", json: { name } });
      setName(""); setOpen(false); onCreated(r.id);
    }}>
      <input autoFocus className="field !py-1" placeholder="Namn, t.ex. Pressbilder" value={name} onChange={(e) => setName(e.target.value)}
        onBlur={() => !name && setOpen(false)} onKeyDown={(e) => e.key === "Escape" && setOpen(false)} />
    </form>
  );
}

function CollectionHeader({ c, total, isAdmin, onExport, onClose, onChanged, onDeleted }: {
  c: Collection; total: number; isAdmin: boolean; onExport: () => void; onClose: () => void; onChanged: () => void; onDeleted: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.name);
  const [desc, setDesc] = useState(c.description);
  useEffect(() => { setName(c.name); setDesc(c.description); }, [c.id]);
  const save = async () => {
    await api(`collections/${c.id}`, { method: "PATCH", json: { name, description: desc } });
    setEditing(false); onChanged();
  };
  return (
    <div className="card mb-4 flex flex-wrap items-start gap-4 p-4">
      <FolderOpen size={22} className="mt-0.5 text-ember" />
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="space-y-2">
            <input className="field font-semibold" value={name} onChange={(e) => setName(e.target.value)} />
            <input className="field" placeholder="Beskrivning (valfri)" value={desc} onChange={(e) => setDesc(e.target.value)} />
            <div className="flex gap-2"><button className="btn-primary" onClick={save}>Spara</button><button className="btn" onClick={() => setEditing(false)}>Avbryt</button></div>
          </div>
        ) : (
          <>
            <div className="text-lg font-semibold">{c.name}</div>
            {c.description && <p className="text-soft">{c.description}</p>}
            <p className="mt-0.5 text-[13px] text-muted">{total} bild{total === 1 ? "" : "er"}{isAdmin && total === 0 ? " – markera bilder och välj ”Lägg i samling”" : ""}</p>
          </>
        )}
      </div>
      {!editing && (
        <div className="flex flex-wrap gap-2">
          {total > 0 && <button className="btn-primary" onClick={onExport}><Download size={14} /> Exportera samlingen</button>}
          {isAdmin && <button className="btn" onClick={() => setEditing(true)}><Pencil size={14} /> Byt namn</button>}
          {isAdmin && <button className="btn !text-bad" onClick={async () => {
            if (!confirm(`Ta bort samlingen ”${c.name}”? Bilderna finns kvar i bildbanken.`)) return;
            await api(`collections/${c.id}`, { method: "DELETE" }); onDeleted();
          }}><Trash2 size={14} /></button>}
          <button className="btn" onClick={onClose} aria-label="Stäng samlingen"><X size={14} /></button>
        </div>
      )}
    </div>
  );
}

/** Ersätter "Att åtgärda"-sidans viktigaste larm: bilder som används på webben och vars licens snart går ut. */
function ExpiringBanner() {
  const [rows, setRows] = useState<{ id: string; title: string; license_expires: string; pages: number }[]>([]);
  useEffect(() => { api<{ usedSoonExpiring: typeof rows }>("issues").then((d) => setRows(d.usedSoonExpiring)).catch(() => {}); }, []);
  if (!rows.length) return null;
  return (
    <div className="mb-4 flex gap-2.5 rounded-xl border border-bad/25 bg-bad-soft px-4 py-3 text-[13.5px]">
      <AlertTriangle size={17} className="mt-0.5 shrink-0 text-bad" />
      <div>
        <b className="text-bad">Licens går ut på bilder som används på webben:</b>{" "}
        {rows.map((r, i) => (
          <span key={r.id}>{i > 0 && ", "}<Link href={`/bild/${r.id}`} className="underline">{r.title || r.id}</Link> ({r.license_expires}, {r.pages} sid{r.pages === 1 ? "a" : "or"})</span>
        ))}
      </div>
    </div>
  );
}

export function Badge({ tone, children }: { tone: "warn" | "bad" | "ok" | "ember" | "muted"; children: React.ReactNode }) {
  const c = { warn: "bg-warn-soft text-warn", bad: "bg-bad-soft text-bad", ok: "bg-ok-soft text-ok", ember: "bg-ember-soft text-ember", muted: "bg-mist text-soft" }[tone];
  return <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11.5px] font-medium ${c}`}>{children}</span>;
}

function Facet({ title, children }: { title: string; children: React.ReactNode }) {
  return <div><div className="h-section mb-1.5">{title}</div><div className="space-y-0.5">{children}</div></div>;
}
function FacetRow({ label, active, onClick, n }: { label: string; active: boolean; onClick: () => void; n?: number }) {
  return (
    <button onClick={onClick} className={`flex w-full items-center justify-between rounded-md px-2 py-1 text-left transition ${active ? "bg-ember-soft font-medium text-ember-2" : "text-soft hover:bg-mist"}`}>
      <span className="truncate">{label}</span>
      {active ? <Check size={14} /> : n != null && <span className="text-muted">{n}</span>}
    </button>
  );
}

function Empty({ filtered }: { filtered: boolean }) {
  const { upload, isAdmin } = useApp();
  if (filtered) return <div className="card p-10 text-center text-soft">Inga bilder matchar. Ta bort ett filter eller sök bredare.</div>;
  if (!isAdmin) return <div className="card p-10 text-center text-soft">Det finns inga publicerade bilder ännu.</div>;
  return (
    <label className="card flex cursor-pointer flex-col items-center border-dashed p-16 text-center hover:bg-mist">
      <div className="text-lg font-semibold">Bildbanken är tom</div>
      <p className="mt-1 max-w-md text-soft">Dra in bilder hit eller klicka för att välja. Varje bild får automatiskt taggförslag, fokuspunkt och en kontroll av personer i bild.</p>
      <input type="file" accept="image/*" multiple hidden onChange={(e) => upload([...(e.target.files ?? [])])} />
    </label>
  );
}

function BulkBar({ ids, collection, onDone, onClear, onExport }: {
  ids: string[]; collection: Collection | null; onDone: () => void; onClear: () => void; onExport: () => void;
}) {
  const { isAdmin } = useApp();
  const [editing, setEditing] = useState(false);
  const [tag, setTag] = useState("");
  const [msg, setMsg] = useState("");
  const run = async (body: object) => {
    const r = await api<{ results: { id: string; error?: string }[] }>("bulk", { method: "POST", json: { ids, ...body } });
    const failed = r.results.filter((x) => x.error);
    if (failed.length) setMsg(`${failed.length} kunde inte ändras: ${failed[0]!.error}`);
    else onDone();
  };
  return (
    <div className="fixed inset-x-0 bottom-4 z-40 mx-auto flex w-fit max-w-[calc(100vw-2rem)] flex-wrap items-center gap-2 rounded-2xl bg-ink px-4 py-3 text-white shadow-2xl">
      <span className="font-medium">{ids.length} markerade</span>
      {isAdmin && <form onSubmit={(e) => { e.preventDefault(); if (tag.trim()) run({ addTags: [tag] }); setTag(""); }} className="flex gap-1">
        <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Lägg till tagg…" className="rounded-lg bg-white/10 px-2.5 py-1 text-white placeholder:text-white/50 outline-none" />
      </form>}
      {isAdmin && <button className="flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 hover:bg-white/20" onClick={() => setEditing(true)}>
        <Pencil size={14} /> Redigera
      </button>}
      {isAdmin && <AddToCollection dark ids={ids} onDone={(m) => { setMsg(m); onDone(); }} />}
      {isAdmin && collection && (
        <button className="flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 hover:bg-white/20" onClick={async () => {
          await api(`collections/${collection.id}/assets`, { method: "DELETE", json: { ids } });
          onDone();
        }}><X size={14} /> Ta bort ur samlingen</button>
      )}
      {isAdmin && <AnalyzeButton ids={ids} onMessage={setMsg} />}
      <button className="flex items-center gap-1.5 rounded-lg bg-ember px-2.5 py-1 font-medium hover:bg-ember-2" onClick={onExport}>
        <Download size={14} /> Exportera
      </button>
      {isAdmin && <>
        <button className="rounded-lg bg-white/10 px-2.5 py-1 hover:bg-white/20" onClick={() => run({ set: { status: "publicerad" } })}>Publicera</button>
        <button className="rounded-lg bg-white/10 px-2.5 py-1 hover:bg-white/20" onClick={() => run({ set: { status: "arkiverad" } })}>Arkivera</button>
      </>}
      <button className="text-white/60 hover:text-white" onClick={onClear}>Avmarkera</button>
      {msg && <span className="w-full text-[13px] text-orange-200">{msg}</span>}
      {editing && <BulkEdit ids={ids} onClose={() => setEditing(false)} onDone={(m) => { setEditing(false); setMsg(m); onDone(); }} />}
    </div>
  );
}
